import { useQuery } from '@tanstack/react-query';

import { CHAIN_IDS, SUPPORTED_CHAIN_IDS } from '@/constants/chain';

import { fetcherWithJwt } from '@/utils/fetcher';
import { getPortfolioRefreshNonce } from '@/utils/portfolioRefresh';
import { retryNetworkOrServerError } from '@/utils/queryClient';
import { routes } from '@/utils/routes';
import type { TokenBalance } from '@/utils/zerion/types';

type WalletBalancesParams = {
  evmAddress?: string;
  solanaAddress?: string;
  chainIds?: number[];
  provider?: 'matcha-api' | 'zerion';
  includeHyperCore?: boolean;
};

export const walletBalancesQueryKey = (params: WalletBalancesParams) =>
  ['wallet-balances', params] as const;

export function useWalletBalances({
  evmAddress,
  solanaAddress,
  chainIds = SUPPORTED_CHAIN_IDS,
  provider = 'matcha-api',
  includeHyperCore = false,
  enabled = true,
  refetchInterval = false,
}: WalletBalancesParams & {
  enabled?: boolean;
  refetchInterval?: number | false;
}) {
  const requestedChainIds = chainIds.filter((id) =>
    id === CHAIN_IDS.SOLANA ? !!solanaAddress : !!evmAddress,
  );

  const params = {
    evmAddress,
    solanaAddress,
    chainIds: requestedChainIds,
    provider,
    includeHyperCore,
  };

  return useQuery<TokenBalance[], Error>({
    queryKey: walletBalancesQueryKey(params),
    queryFn: async () => {
      const query = new URLSearchParams({
        provider,
        chainIds: requestedChainIds.join(','),
        includeHyperCore: String(includeHyperCore),
      });

      if (evmAddress) query.set('evmAddress', evmAddress);
      if (solanaAddress) query.set('solanaAddress', solanaAddress);

      const refreshNonce = getPortfolioRefreshNonce();
      if (refreshNonce) query.set('_refresh', refreshNonce);

      const url = `${routes.api.WALLET_BALANCES}?${query}`;

      const response = await fetcherWithJwt<{ balances: TokenBalance[] }>(url);
      return response.balances;
    },
    enabled: enabled && !!(evmAddress || solanaAddress),
    staleTime: refetchInterval || 30_000,
    retry: retryNetworkOrServerError,
    refetchInterval,
    refetchOnWindowFocus: !!refetchInterval,
  });
}
